import * as React from "react";
import Modal from "../../../ui/modal/Modal";
import {connect} from "react-redux";
import {none, Option, option} from "ts-option";
import {RootState} from "../../../../store/reducer/root/RootState";
import JobType from "../../../../model/job/JobType";
import Loading from "../../../ui/loading/Loading";
import {api} from "../../../../api/api";
import {isDefined, notifyBugsnagAndShowToast} from "../../../../utils/Utils";
import ButtonTertiary from "../../../../hiberUiKit/src/atoms/Buttons/ButtonTertiary";
import ButtonPrimary from "../../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import Switch from "../../../../hiberUiKit/src/atoms/Switch/Switch";
import "../../shared/jobTypeModal.scss"
import {toKeyIndexedObject} from "../../../../utils/ArrayUtils";
import Toast from "../../../helper/Toast";
import Row from "../../../layout/Row";
import Col from "../../../layout/Col";
import RangeNumberInput from "../../../../hiberUiKit/src/atoms/Input/RangeNumberInput";
import Utility from "../../../../model/utility/Utility";
import {fetchUtilities} from "../../../../store/action/utility/utility";
import SelectUtility from "../../utilities/form/SelectUtility";

interface IComponentProps {
	onFinish: () => void;
	partnerBrandId: string
}

interface ReduxProps {
	jobTypes: JobType[]
	jobTypesMap: { [key: string]: JobType }
	dispatch: any
}

interface IState {
	pbJobTypes: Option<JobType[]>
	processing: boolean,
	notValid: boolean
}

type Props = IComponentProps & ReduxProps;

class PartnerBrandJobTypesConfigModal extends React.Component<Props, IState> {
	constructor(props: Props) {
		super(props);
		this.state = {
			pbJobTypes: none,
			processing: false,
			notValid: false
		};

		this.renderJobTypes = this.renderJobTypes.bind(this);
		this.changeJobTypeActiveProperty = this.changeJobTypeActiveProperty.bind(this);
		this.saveConfiguration = this.saveConfiguration.bind(this);
		this.takeJobTypesFromGlobalConfigIfNotExists = this.takeJobTypesFromGlobalConfigIfNotExists.bind(this);
	}

	componentDidMount() {
		this.props.dispatch(fetchUtilities());
		api.instance.partnerBrandApi.fetchJobTypesConfig(this.props.partnerBrandId)
			.then(jobTypes => this.setState({pbJobTypes: option(this.takeJobTypesFromGlobalConfigIfNotExists(jobTypes))}))
			.catch(err => notifyBugsnagAndShowToast(err, "Fetching job type config error"))
	}

	takeJobTypesFromGlobalConfigIfNotExists(pbJobTypes: JobType[]) {
		const pbJobTypesMap = toKeyIndexedObject(pbJobTypes, j => j.id);
		return this.props.jobTypes.map(jt => pbJobTypesMap[jt.id] ? pbJobTypesMap[jt.id] : jt.copy({active: false}));
	}

	render() {
		return (
			<Modal onCloseClicked={this.props.onFinish} fixedWidth oveflowUnset title={"Partner brand job type configuration"}>
				<Loading isLoading={this.state.pbJobTypes.isEmpty}>
					{this.renderJobTypes()}
					<div className="c-job-type-modal-table_buttons-bar">
						<ButtonTertiary label={"Cancel"} onClick={this.props.onFinish}/>
						<ButtonPrimary isLoading={this.state.processing}
									   disabled={this.state.notValid}
									   label={"Save configuration"}
									   onClick={this.saveConfiguration}/>
					</div>
				</Loading>
			</Modal>
		);
	}

	renderJobTypes() {
		if (this.state.pbJobTypes.isDefined) {
			return (
				<div className={"c-job-type-modal-table"}>
					<Row customClass={"header"}>
						<Col xl={3} md={3}>Name</Col>
						<Col xl={2} md={2}>Active</Col>
						<Col xl={2} md={2}>Fee</Col>
						<Col xl={3} md={3}>Utility</Col>
						<Col xl={2} md={2}/>
					</Row>
					{this.state.pbJobTypes.map(jobTypes =>
						jobTypes.map(jt => {
							const globalJT = this.props.jobTypesMap[jt.id];
							const showWarning = jt.active && globalJT && !globalJT.active;
							const showRequiredWarning = !isDefined(jt.fee);

							return <Row key={jt.id}>
								<Col xl={3} md={3}>{jt.name}</Col>
								<Col xl={2} md={2}>
									<Switch checked={jt.active}
											onChange={value => this.changeJobTypeActiveProperty(jt.id, value)}/>
								</Col>
								<Col xl={2} md={2}>
									<RangeNumberInput
										onChange={value => this.changeJobTypeFeeProperty(jt.id, value)}
										value={option(jt.fee)}
										minValue={option(0)}
										maxValue={option(100)}
										disabled={!jt.active}
										maxDecimalPlaces={2}/>
								</Col>
								<Col xl={3} md={3}>
									<SelectUtility
										placeholder={"Brand default"}
										selectedId={option(jt.utilityId)}
										onChanged={(u: Option<Utility>) => this.changeJobTypeUtilityProperty(jt.id, u)}
										clearable={true}
										portalMenu={true}/>
								</Col>
								<Col xl={2} md={2}>
									<span className={"c-job-type-modal-table_warning"}>
										{showWarning && <span className={"text"}>This job type is globally disabled!</span>}
										{showRequiredWarning &&
										<span className={"text"}>Fee is required. Please fill the input.</span>}
									</span>
								</Col>
							</Row>
						})
					).orNull}
				</div>
			)
		}
		return null;
	}

	changeJobTypeFeeProperty(jobTypeId: string, newValue: Option<number>) {
		const pbJobTypes = this.state.pbJobTypes.map(
			jobTypes => jobTypes.map(jt => jt.id === jobTypeId ? jt.copy({fee: newValue.orNull}) : jt)).getOrElse([]);
		this.setState({pbJobTypes: option(pbJobTypes), notValid: newValue.isEmpty});
	}

	changeJobTypeUtilityProperty(jobTypeId: string, utility: Option<Utility>) {
		const pbJobTypes = this.state.pbJobTypes.map(
			jobTypes => jobTypes.map(
				jt => jt.id === jobTypeId ? jt.withUtility(utility.isDefined ? utility.get.id : undefined) : jt))
			.getOrElse([]);
		this.setState({pbJobTypes: option(pbJobTypes)});
	}

	changeJobTypeActiveProperty(jobTypeId: string, newValue: boolean) {
		const pbJobTypes = this.state.pbJobTypes.map(
			jobTypes => jobTypes.map(
				jt => jt.id === jobTypeId ? jt.copy({active: newValue, fee: isDefined(jt.fee) ? jt.fee : 0}) : jt))
			.getOrElse([]);
		this.setState({pbJobTypes: option(pbJobTypes)});
	}

	saveConfiguration() {
		const updateJobTypes = this.state.pbJobTypes.map(jobTypes => jobTypes.map(jt => jt.toPbUpdateJson())).getOrElse([]);
		this.setState({processing: true}, () => {
			api.instance.partnerBrandApi.updateJobTypesConfig(this.props.partnerBrandId, updateJobTypes)
				.then(() => {
					this.setState({processing: false}, () => {
						this.props.onFinish();
						Toast.success("Partner brand job types configuration updated",
							"Update partner brand job types operation success");
					})
				})
				.catch(err => {
					this.setState({processing: false},
						() => notifyBugsnagAndShowToast(err, "Update partner brand job types operation failed"))
				})
		})
	}
}

function mapStateToProps(state: RootState) {
	const jobTypes = state.jobType.jobTypes.getOrElse([]);
	return {
		jobTypes,
		jobTypesMap: toKeyIndexedObject(jobTypes, jt => jt.id)
	}
}

export default connect(mapStateToProps)(PartnerBrandJobTypesConfigModal);
